import React, { useState, useEffect } from 'react';
import { 
    List, 
    useListContext,
    useDataProvider,
    TopToolbar,
    Edit,
    SimpleForm,
    useRecordContext,
    useNotify,
    useRedirect,
    ListButton
} from 'react-admin';
import { 
    Box, 
    Grid, 
    Typography, 
    Paper, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button,
    Chip,
    TextField as MuiTextField
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditCalendarRoundedIcon from '@mui/icons-material/EditCalendarRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import UploadRoundedIcon from '@mui/icons-material/UploadRounded';
import { uploadEventImage, deleteEventMedia } from './s3Service';
import DetailLoadingState from './components/DetailLoadingState';
import { ImageCropDialog } from './components/ImageCropDialog';

const APP_CARD_WIDTH = 361;
const APP_CARD_HEIGHT = 145;
const EVENT_CARD_ASPECT = APP_CARD_WIDTH / APP_CARD_HEIGHT;
const EVENT_EXPORT_WIDTH = APP_CARD_WIDTH * 5;
const EVENT_EXPORT_HEIGHT = APP_CARD_HEIGHT * 5;

// Компонент для отображения списка мероприятий
const EventsDatagrid = () => {
    const { data } = useListContext();
    
    // Форматирование даты в локальный часовой пояс пользователя (с секундами)
    const formatLocalDateTime = (dateString) => {
        if (!dateString) return '';
        try {
            // Если дата не содержит информацию о часовом поясе, добавляем 'Z' для UTC
            let isoString = dateString;
            if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
                // Проверяем, есть ли 'T' в строке
                if (dateString.includes('T')) {
                    // Формат: YYYY-MM-DDTHH:MM:SS (без Z)
                    isoString = dateString + 'Z';
                } else {
                    // Формат: YYYY-MM-DD HH:MM:SS
                    isoString = dateString.replace(' ', 'T') + 'Z';
                }
            }
            
            const date = new Date(isoString);
            if (isNaN(date.getTime())) return '';
            
            // Используем локальный часовой пояс пользователя (браузер определяет автоматически)
            // Добавляем секунды для соответствия формату в подробной информации
            return date.toLocaleString('ru-RU', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        } catch (error) {
            console.error('Error formatting date:', error, 'Input:', dateString);
            return dateString; // Возвращаем исходную строку в случае ошибки
        }
    };
    
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
            {(data || []).map((record) => (
                <Paper key={record.id} component="a" href={`#/events/${record.id}`} elevation={0} sx={{ overflow: 'hidden', border: '1px solid', borderColor: 'divider', borderRadius: 3, color: 'inherit', textDecoration: 'none', transition: '160ms ease', '&:hover': { borderColor: 'primary.main', transform: 'translateY(-3px)', boxShadow: '0 14px 34px rgba(15, 23, 42, .10)' }, '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.light', outlineOffset: 2 } }}>
                    <Box sx={{ aspectRatio: `${APP_CARD_WIDTH} / ${APP_CARD_HEIGHT}`, bgcolor: 'action.hover', position: 'relative', overflow: 'hidden' }}>
                        {record.image_url ? <Box component="img" src={record.image_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', color: 'text.disabled' }}><ImageRoundedIcon sx={{ fontSize: 44 }} /></Box>}
                        <Box sx={{ position: 'absolute', top: 12, right: 12, px: 1.25, py: .5, borderRadius: 99, bgcolor: 'rgba(15,23,42,.78)', color: '#fff', fontSize: 12, fontWeight: 750 }}>#{record.id}</Box>
                    </Box>
                    <Box sx={{ p: 2.25 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25, mb: 1.5 }}>{record.name || 'Без названия'}</Typography>
                        <Box sx={{ display: 'grid', gap: .85 }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><CalendarMonthRoundedIcon color="primary" fontSize="small" /><Typography variant="body2">{formatLocalDateTime(record.event_date) || 'Дата не указана'}</Typography></Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><ScheduleRoundedIcon color="action" fontSize="small" /><Typography variant="body2" color="text.secondary">{record.event_time || 'Время не указано'}</Typography></Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><LocationOnRoundedIcon color="action" fontSize="small" /><Typography variant="body2" color="text.secondary" noWrap>{record.position || 'Место не указано'}</Typography></Box>
                        </Box>
                    </Box>
                </Paper>
            ))}
        </Box>
    );
};

// Кастомные actions для списка с кнопкой создания
const EventsListActions = () => {
    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    return (
        <>
            <TopToolbar>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDialogOpen(true)}
                    sx={{ fontWeight: 600 }}
                >
                    Создать мероприятие
                </Button>
            </TopToolbar>
            <CreateEventDialog 
                open={createDialogOpen} 
                onClose={() => setCreateDialogOpen(false)} 
            />
        </>
    );
};

// Диалог создания мероприятия
const CreateEventDialog = ({ open, onClose }) => {
    const [name, setName] = useState('');
    const [creating, setCreating] = useState(false);
    
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();

    const handleCreate = async () => {
        if (!name) {
            notify('Название обязательно', { type: 'warning' });
            return;
        }

        setCreating(true);

        try {
            // Получаем логин авторизованного пользователя
            const username = localStorage.getItem('username') || '';
            
            // Текущая дата и время в UTC
            const now = new Date().toISOString();

            const result = await dataProvider.create('events', {
                data: {
                    name: name,
                    position: '',
                    user_id: username,
                    event_date: now,
                    event_time: '00:00:00',
                    description: '',
                    tags: [],
                    image_url: ''
                }
            });
            
            notify('Мероприятие успешно создано', { type: 'success' });
            handleClose();
            redirect('edit', 'events', result.data.id);
        } catch (error) {
            console.error('Error creating event:', error);
            notify(`Ошибка при создании: ${error.message}`, { type: 'error' });
        } finally {
            setCreating(false);
        }
    };

    const handleClose = () => {
        setName('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 800 }}>Новое мероприятие</DialogTitle>
            <DialogContent>
                <Typography color="text.secondary" sx={{ mb: 2 }}>Создайте карточку, затем добавьте расписание, описание и обложку.</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <MuiTextField
                        label="Название *"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        required
                        autoFocus
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Отмена</Button>
                <Button 
                    onClick={handleCreate} 
                    variant="contained" 
                    disabled={!name || creating}
                >
                    {creating ? 'Создание...' : 'Создать'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Пустой компонент для отображения когда нет данных
const EmptyEventsList = () => {
    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    return (
        <>
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: 'calc(100vh - 200px)',
                gap: 2
            }}>
                <Typography variant="h6" color="text.secondary">
                    Нет мероприятий
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDialogOpen(true)}
                >
                    Создать мероприятие
                </Button>
            </Box>
            <CreateEventDialog 
                open={createDialogOpen} 
                onClose={() => setCreateDialogOpen(false)} 
            />
        </>
    );
};

// Компонент списка мероприятий
export const EventsList = (props) => (
    <List 
        {...props}
        exporter={false}
        title="Мероприятия"
        perPage={30}
        sort={{ field: 'id', order: 'DESC' }}
        actions={<EventsListActions />}
        empty={<EmptyEventsList />}
        sx={{ '& .RaList-actions': { mb: 1 }, '& .RaList-content': { boxShadow: 'none', bgcolor: 'transparent' } }}
    >
        <Box sx={{ mb: 3 }}><Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: '-.03em', mb: .75 }}>Мероприятия</Typography><Typography color="text.secondary">Афиша зоопарка: расписание, места проведения и карточки событий.</Typography></Box>
        <EventsDatagrid />
    </List>
);

// Кастомные actions для страницы редактирования
const EventsEditActions = () => (
    <TopToolbar>
        <ListButton label="К афише" icon={<ArrowBackIcon />} />
    </TopToolbar>
);

// Контент формы редактирования
const EventsEditContent = () => {
    const record = useRecordContext();
    const [name, setName] = useState('');
    const [position, setPosition] = useState('');
    const [eventDate, setEventDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [description, setDescription] = useState('');
    const [tags, setTags] = useState([]);
    const [tagsInput, setTagsInput] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [cropSourceFile, setCropSourceFile] = useState(null);
    const [cropDialogOpen, setCropDialogOpen] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();

    useEffect(() => {
        if (record) {
            setName(record.name || '');
            setPosition(record.position || '');
            setDescription(record.description || '');
            setImageUrl(record.image_url || '');
            
            // Обработка тегов
            const recordTags = record.tags || [];
            setTags(Array.isArray(recordTags) ? recordTags : []);
            setTagsInput(Array.isArray(recordTags) ? recordTags.join(', ') : '');
            
            // Обработка даты мероприятия - конвертируем из UTC в локальное время
            if (record.event_date) {
                try {
                    let isoString = record.event_date;
                    if (!record.event_date.endsWith('Z') && !record.event_date.includes('+') && !record.event_date.includes('T')) {
                        isoString = record.event_date.replace(' ', 'T') + 'Z';
                    } else if (record.event_date.includes('T') && !record.event_date.endsWith('Z') && !record.event_date.includes('+')) {
                        isoString = record.event_date + 'Z';
                    }
                    
                    const date = new Date(isoString);
                    if (!isNaN(date.getTime())) {
                        // Форматируем в локальное время для datetime-local input
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        setEventDate(`${year}-${month}-${day}T${hours}:${minutes}`);
                    }
                } catch (error) {
                    console.error('Error parsing event_date:', error);
                }
            }
            
            // Обработка времени мероприятия
            const timeString = record.event_time || '00:00:00';
            
            // Парсим время в формат HH:MM-HH:MM если есть дефис
            if (timeString.includes('-')) {
                const [start, end] = timeString.split('-');
                setStartTime(start.trim());
                setEndTime(end.trim());
            } else {
                // Если формат старый (HH:MM:SS), берем только HH:MM
                const timeParts = timeString.split(':');
                if (timeParts.length >= 2) {
                    setStartTime(`${timeParts[0]}:${timeParts[1]}`);
                }
                setEndTime('');
            }
        }
    }, [record]);

    const handleSave = async () => {
        if (!name) {
            notify('Название обязательно', { type: 'warning' });
            return;
        }

        setUpdating(true);

        try {
            // Конвертируем локальное время обратно в UTC для отправки на сервер
            let eventDateUTC = record.event_date;
            if (eventDate) {
                const localDate = new Date(eventDate);
                eventDateUTC = localDate.toISOString();
            }
            
            // Формируем строку времени в формате HH:MM-HH:MM или HH:MM
            let timeString = startTime;
            if (endTime) {
                timeString = `${startTime}-${endTime}`;
            }
            
            await dataProvider.update('events', {
                id: record.id,
                data: {
                    name: name,
                    position: position || '',
                    user_id: record.user_id, // Отправляем оригинальное значение из записи
                    event_date: eventDateUTC,
                    event_time: timeString,
                    description: description || '',
                    tags: tags,
                    image_url: imageUrl || ''
                },
                previousData: record
            });
            
            notify('Мероприятие успешно обновлено', { type: 'success' });
            redirect('list', 'events');
        } catch (error) {
            console.error('Error updating event:', error);
            notify(`Ошибка при сохранении: ${error.message}`, { type: 'error' });
        } finally {
            setUpdating(false);
        }
    };

    const handleDelete = async () => {
        try {
            await dataProvider.delete('events', {
                id: record.id,
                previousData: record
            });
            
            notify('Мероприятие успешно удалено', { type: 'success' });
            setDeleteDialogOpen(false);
            redirect('list', 'events');
        } catch (error) {
            console.error('Error deleting event:', error);
            notify(`Ошибка при удалении: ${error.message}`, { type: 'error' });
            setDeleteDialogOpen(false);
        }
    };

    // Функция для извлечения имени файла из URL
    const extractFileNameFromUrl = (url) => {
        if (!url) return null;
        try {
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/');
            return pathParts[pathParts.length - 1]; // Последняя часть пути - имя файла
        } catch (error) {
            console.error('Error extracting filename from URL:', error);
            return null;
        }
    };

    // Обработка загрузки изображения (одно изображение)
    const uploadCroppedEventImage = async (file) => {
        if (!record || !record.id) {
            notify('Ошибка: ID мероприятия не найден', { type: 'error' });
            return;
        }

        setUploading(true);

        try {
            // Используем название мероприятия как префикс
            const prefix = name.replace(/[^a-zA-Zа-яА-Я0-9]/g, '_') || `event_${record.id}`;
            const url = await uploadEventImage(file, prefix);
            
            setImageUrl(url);
            
            // Автоматически сохраняем мероприятие с новым URL изображения
            let eventDateUTC = record.event_date;
            if (eventDate) {
                const localDate = new Date(eventDate);
                eventDateUTC = localDate.toISOString();
            }
            
            // Формируем строку времени
            let timeString = startTime;
            if (endTime) {
                timeString = `${startTime}-${endTime}`;
            }
            
            await dataProvider.update('events', {
                id: record.id,
                data: {
                    name: name,
                    position: position || '',
                    user_id: record.user_id,
                    event_date: eventDateUTC,
                    event_time: timeString,
                    description: description || '',
                    tags: tags,
                    image_url: url
                },
                previousData: record
            });
            
            notify('Изображение успешно загружено', { type: 'success' });
        } catch (error) {
            console.error('Error uploading image:', error);
            notify(`Ошибка загрузки изображения: ${error.message}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setCropSourceFile(file);
        setCropDialogOpen(true);
        e.target.value = '';
    };

    const handleCropCancel = () => {
        setCropDialogOpen(false);
        setCropSourceFile(null);
    };

    const handleCropApply = async (croppedFile, error = null) => {
        if (error) {
            notify(`Ошибка обрезки: ${error.message}`, { type: 'error' });
            handleCropCancel();
            return;
        }

        if (croppedFile) {
            await uploadCroppedEventImage(croppedFile);
        }
        handleCropCancel();
    };

    // Обработка удаления изображения
    const handleDeleteImage = async () => {
        if (!imageUrl) return;

        setUploading(true);

        try {
            const fileName = extractFileNameFromUrl(imageUrl);
            if (fileName) {
                await deleteEventMedia(fileName);
            }
            
            setImageUrl('');
            
            // Автоматически сохраняем мероприятие без URL изображения
            let eventDateUTC = record.event_date;
            if (eventDate) {
                const localDate = new Date(eventDate);
                eventDateUTC = localDate.toISOString();
            }
            
            // Формируем строку времени
            let timeString = startTime;
            if (endTime) {
                timeString = `${startTime}-${endTime}`;
            }
            
            await dataProvider.update('events', {
                id: record.id,
                data: {
                    name: name,
                    position: position || '',
                    user_id: record.user_id,
                    event_date: eventDateUTC,
                    event_time: timeString,
                    description: description || '',
                    tags: tags,
                    image_url: ''
                },
                previousData: record
            });
            
            notify('Изображение удалено', { type: 'success' });
        } catch (error) {
            console.error('Error deleting image:', error);
            notify(`Ошибка удаления изображения: ${error.message}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    // Форматирование даты в локальный часовой пояс пользователя
    const formatLocalDateTime = (dateString) => {
        if (!dateString) return '';
        try {
            // Если дата не содержит информацию о часовом поясе, добавляем 'Z' для UTC
            let isoString = dateString;
            if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('T')) {
                // Формат: YYYY-MM-DD HH:MM:SS
                isoString = dateString.replace(' ', 'T') + 'Z';
            } else if (dateString.includes('T') && !dateString.endsWith('Z') && !dateString.includes('+')) {
                // Формат: YYYY-MM-DDTHH:MM:SS (без Z)
                isoString = dateString + 'Z';
            }
            
            const date = new Date(isoString);
            if (isNaN(date.getTime())) return '';
            
            // Используем локальный часовой пояс пользователя (браузер определяет автоматически)
            return date.toLocaleString('ru-RU', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    // Обработка изменения тегов
    const handleTagsChange = (e) => {
        const value = e.target.value;
        setTagsInput(value);
        // Разделяем по запятой и убираем пробелы
        const tagsArray = value.split(',').map(tag => tag.trim()).filter(tag => tag);
        setTags(tagsArray);
    };

    // Валидация времени окончания
    const handleEndTimeChange = (e) => {
        const newEndTime = e.target.value;
        
        if (!startTime) {
            notify('Сначала выберите время начала', { type: 'warning' });
            return;
        }
        
        // Сравниваем времена
        if (newEndTime && newEndTime <= startTime) {
            notify('Время окончания должно быть больше времени начала', { type: 'warning' });
            return;
        }
        
        setEndTime(newEndTime);
    };
    
    if (!record) {
        return <DetailLoadingState title="Загружаем данные мероприятия..." />;
    }
    
    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>Мероприятие #{record.id}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: '-.03em' }}>{name || 'Новое мероприятие'}</Typography>
                <Typography color="text.secondary" sx={{ mt: .75 }}>Заполните карточку события так, как её увидят посетители.</Typography>
            </Box>
            <Grid container spacing={3}>
                <Grid item xs={12} lg={8}>
                    <Box sx={{ display: 'grid', gap: 3 }}>
                        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, mb: .5 }}>Основная информация</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>Короткое понятное название, место и описание программы.</Typography>
                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <MuiTextField label="Название" value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
                                <MuiTextField label="Место проведения" value={position} onChange={(e) => setPosition(e.target.value)} fullWidth placeholder="Например: Главная площадка" />
                                <MuiTextField label="Описание" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline minRows={6} maxRows={20} placeholder="Что ждёт посетителя на мероприятии?" />
                                <MuiTextField label="Теги" value={tagsInput} onChange={handleTagsChange} fullWidth placeholder="детям, семейное, познавательное" helperText="Разделяйте теги запятыми" />
                            </Box>
                        </Paper>
                        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2.5 }}><EditCalendarRoundedIcon color="primary" /><Box><Typography variant="h6" sx={{ fontWeight: 800 }}>Расписание</Typography><Typography variant="body2" color="text.secondary">Дата публикации в афише и фактический интервал события.</Typography></Box></Box>
                            <MuiTextField label="Дата мероприятия" type="datetime-local" value={eventDate} onChange={(e) => setEventDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}><MuiTextField label="Начало" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
                                <Grid item xs={12} sm={6}><MuiTextField label="Окончание" type="time" value={endTime} onChange={handleEndTimeChange} fullWidth disabled={!startTime} InputLabelProps={{ shrink: true }} inputProps={{ min: startTime || undefined }} /></Grid>
                            </Grid>
                            {startTime && <Chip icon={<ScheduleRoundedIcon />} label={endTime ? `${startTime}–${endTime}` : startTime} color="primary" variant="outlined" sx={{ mt: 2 }} />}
                        </Paper>
                    </Box>
                </Grid>
                <Grid item xs={12} lg={4}>
                    <Box sx={{ display: 'grid', gap: 3, position: { lg: 'sticky' }, top: { lg: 88 } }}>
                        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, mb: .5 }}>Обложка</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Формат карточки 361 × 145. Обрезка откроется перед загрузкой.</Typography>
                            <Box sx={{ aspectRatio: `${APP_CARD_WIDTH} / ${APP_CARD_HEIGHT}`, borderRadius: 2.5, overflow: 'hidden', bgcolor: 'action.hover', display: 'grid', placeItems: 'center', mb: 2 }}>
                                {imageUrl ? <Box component="img" src={imageUrl} alt="Обложка мероприятия" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <ImageRoundedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
                            </Box>
                            <Button variant="outlined" component="label" fullWidth startIcon={<UploadRoundedIcon />} disabled={uploading}>{uploading ? 'Загружаем…' : imageUrl ? 'Заменить обложку' : 'Загрузить обложку'}<input type="file" hidden accept="image/*" onChange={handleImageUpload} disabled={uploading} /></Button>
                            {imageUrl && <Button color="error" fullWidth sx={{ mt: 1 }} onClick={handleDeleteImage} disabled={uploading} startIcon={<DeleteOutlineRoundedIcon />}>Удалить обложку</Button>}
                        </Paper>
                        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>Публикация</Typography>
                            <Box sx={{ py: 2, my: 2, borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider', display: 'grid', gap: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}><Typography variant="body2" color="text.secondary">Создано</Typography><Typography variant="body2">{formatLocalDateTime(record.created_at) || '—'}</Typography></Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}><Typography variant="body2" color="text.secondary">Обновлено</Typography><Typography variant="body2">{formatLocalDateTime(record.updated_at) || '—'}</Typography></Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}><Typography variant="body2" color="text.secondary">Автор</Typography><Typography variant="body2">{record.updated_by_user_id || record.user_id || '—'}</Typography></Box>
                            </Box>
                            <Button variant="contained" size="large" fullWidth onClick={handleSave} disabled={!name || updating || uploading}>{updating ? 'Сохраняем…' : 'Сохранить мероприятие'}</Button>
                            <Button color="error" fullWidth sx={{ mt: 1 }} onClick={() => setDeleteDialogOpen(true)} disabled={updating || uploading}>Удалить мероприятие</Button>
                        </Paper>
                    </Box>
                </Grid>
            </Grid>
            
            {/* Диалог подтверждения удаления */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ color: 'error.main', fontWeight: 600 }}>
                    Подтверждение удаления
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Вы уверены, что хотите удалить это мероприятие?
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Название:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                            {name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            ID:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {record?.id}
                        </Typography>
                    </Paper>
                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                        Это действие нельзя отменить!
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setDeleteDialogOpen(false)}
                        variant="outlined"
                    >
                        Отмена
                    </Button>
                    <Button 
                        onClick={handleDelete}
                        variant="contained"
                        color="error"
                        autoFocus
                    >
                        Удалить
                    </Button>
                </DialogActions>
            </Dialog>

            <ImageCropDialog
                open={cropDialogOpen}
                file={cropSourceFile}
                title="Обрезка изображения мероприятия"
                helperText="Размер контейнера в приложении: 361x145. Итог загружается с запасом качества."
                aspectRatio={EVENT_CARD_ASPECT}
                outputWidth={EVENT_EXPORT_WIDTH}
                outputHeight={EVENT_EXPORT_HEIGHT}
                onCancel={handleCropCancel}
                onApply={handleCropApply}
            />
        </Box>
    );
};

// Компонент редактирования мероприятия
export const EventsEdit = () => {
    return (
        <Edit title="Редактирование мероприятия" actions={<EventsEditActions />}>
            <SimpleForm
                toolbar={false}
                sx={{
                    maxWidth: 'none',
                    width: '100%',
                    '& .RaSimpleForm-form': {
                        maxWidth: 'none',
                        width: '100%',
                    },
                    '& .RaSimpleForm-stack': {
                        maxWidth: 'none',
                        width: '100%',
                        alignItems: 'stretch',
                    },
                }}
            >
                <EventsEditContent />
            </SimpleForm>
        </Edit>
    );
};
